/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { DescriptionPage_CourseType as CourseType } from '__generated__/graphql-types';

import a11yKeyPress from 'js/lib/a11yKeyPress';
import { setLocation } from 'js/lib/coursera.redirect';
import useRouter from 'js/lib/useRouter';
import user, { isAuthenticatedUser } from 'js/lib/user';

import { breakpoints, useTheme } from '@coursera/cds-core';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { getClipsTrackingInfo } from 'bundles/clips/utils/clipsUtils';
import StartDateButton from 'bundles/enterprise-legacy-learner-home/components/StartDateButton';
import ProgramActionButtonContainer from 'bundles/enterprise-legacy-learner-home/components/programActionButton/ProgramActionButtonContainer';
import useEnterpriseBadgeEnrollMutation from 'bundles/enterprise-legacy-learner-home/queryHooks/useEnterpriseBadgeEnrollMutation';
import GrowthDiscoveryExperiments from 'bundles/epic/clients/GrowthDiscovery';
import ProgramMembershipsV2 from 'bundles/naptimejs/resources/programMemberships.v2';
import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import {
  useIsUpswell,
  useProgram,
  useProgramMembership,
} from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';
import EnterpriseDisplayEnrollButton from 'bundles/unified-description-page-common/components/enroll/enterprise/EnterpriseDisplayEnrollButton';
import EnterpriseMultiProgramEnrollButtonNotConfigurable from 'bundles/unified-description-page-common/components/enroll/enterprise/EnterpriseMultiProgramEnrollButtonNotConfigurable';
import { EnterpriseOptimizedEnrollmentButton } from 'bundles/unified-description-page-common/components/enroll/enterprise/EnterpriseOptimizedEnrollmentButton';
import SponsoredByMessage from 'bundles/unified-description-page-common/components/enroll/enterprise/SponsoredByMessage';
import buttonStyles from 'bundles/unified-description-page-common/components/enroll/enterprise/enterpriseEnrollButtonOverrideStyles';
import type { EnrollButtonSize } from 'bundles/unified-description-page-common/components/enroll/enterprise/enterpriseEnrollButtonOverrideStyles';
import { ENROLL_PRODUCT_TYPES } from 'bundles/unified-description-page-common/constants/enterprise';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';
import { useAvailableEnrollmentOptions } from 'bundles/unified-description-page-common/contexts/enterprise/EnterpriseAvailableEnrollmentProgramsContext';
import { useEnterpriseCoursePageData } from 'bundles/unified-description-page-common/contexts/enterprise/EnterpriseBffCourseContext';
import { useEnterpriseLearnerFlowOptimization } from 'bundles/unified-description-page-common/contexts/enterprise/EnterpriseLearnerFlowOptimizationContext';
import useContinueWithCourse from 'bundles/unified-description-page-common/hooks/useContinueWithCourse';
import { getCourseProductUrl } from 'bundles/unified-description-page-common/utils';

type BaseProps = {
  productId?: string | null;
  shouldShowSponsoredByMessage?: boolean;
  size?: EnrollButtonSize;
};

type CourseButtonProps = BaseProps & {
  courseType?: CourseType | null;
  slug: string;
  buttonLabel: string;
};

const styles = {
  button: css`
    display: inline-block;
  `,
  biggerButton: css`
    display: inline-block;

    ${breakpoints.up('sm')} {
      button {
        padding-left: var(--cds-spacing-1000);
        padding-right: var(--cds-spacing-1000);
      }
    }
  `,
};

const ENROLL_BUTTON_TRACKING_ID = 'unified_enterprise_enroll_button';

const EnterpriseCourseEnrollButtonNotConfigurable = ({
  productId,
  slug,
  shouldShowSponsoredByMessage,
  courseType,
  buttonLabel,
  size,
}: CourseButtonProps) => {
  const program = useProgram();
  const availableEnrollmentOptions = useAvailableEnrollmentOptions();
  const pageData = useEnterpriseCoursePageData();
  const { showOptimizedLearnerFlow, onJoiningSuccess } = useEnterpriseLearnerFlowOptimization();
  const thirdPartyOrganization = useThirdPartyOrganization();
  const isUpswell = useIsUpswell();
  const { isProgramMember: isMember, membership } = useProgramMembership();
  const enrollInBadge = useEnterpriseBadgeEnrollMutation();
  const programMembershipV2Instance = new ProgramMembershipsV2(membership || {});
  const isEligibleToJoinProgram = programMembershipV2Instance.isEligibleToJoinProgram;
  const theme = useTheme();
  const router = useRouter();
  const productFeatures = useEnterpriseProductFeatures();
  const track = useTracker();
  const eventingProductData = useEventingProductData();
  const { deepLinkItemId, badgeTemplateId } = router.location.query;
  const { shouldShowGoToCourseButton } = useContinueWithCourse();
  const baseHomeUrl = getCourseProductUrl(slug, false);
  const showQuickEnrollButton = isEligibleToJoinProgram && showOptimizedLearnerFlow;

  const onActionSuccess = async () => {
    if (showOptimizedLearnerFlow && onJoiningSuccess) {
      onJoiningSuccess();
    } else if (deepLinkItemId) {
      setLocation(`${baseHomeUrl}/item/${deepLinkItemId}`);
    } else if (badgeTemplateId) {
      try {
        await enrollInBadge({
          // eslint-disable-next-line
          user_id: user.get().id,
          // eslint-disable-next-line camelcase
          badge_template_id: badgeTemplateId,
          // eslint-disable-next-line camelcase
          request_origin: 'REQUEST_ORIGIN_USER',
        });
      } catch {
        // An error here indicates that the badge was already enrolled in.
        // Silently fail as enrollling in the badge shouldn't prevent the user from accessing the course.
      }
      setLocation(`${baseHomeUrl}/home`);
    } else {
      setLocation(`${baseHomeUrl}/home`);
    }
  };

  const enrollButtonTrackingData = {
    button: {
      name: shouldShowGoToCourseButton ? ('go_to_course' as const) : ('enroll' as const),
      ...(showQuickEnrollButton ? { linkName: 'first_course_item' } : {}),
    },
    enterprise: {
      organizationSlug: thirdPartyOrganization?.slug,
      programSlug: program?.metadata?.slug,
      contractId: program?.contractId as string | undefined,
    },
    ...eventingProductData,
  };

  const enrollButtonRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_button',
    enrollButtonTrackingData
  );

  const handleEnrollButtonClick = () => {
    track('click_button', enrollButtonTrackingData);
  };

  const shouldShowBiggerCTA = size !== 'small' && GrowthDiscoveryExperiments.get('showBiggerCTAonXDP');
  // Do not show any enroll button for programs that are "browse only".
  // This needs to supercede any button that shows here.

  if (productFeatures?.isBrowseOnlyProgram) {
    return null;
  } else if (!isAuthenticatedUser()) {
    return (
      <EnterpriseDisplayEnrollButton
        courseId={pageData?.id}
        // @ts-expect-error DateTime: string
        privateSessionStart={pageData?.fixedSession?.startsAt}
        size={size}
        shouldShowSponsoredByMessage={shouldShowSponsoredByMessage}
        onClick={() => {
          track('click_button', enrollButtonTrackingData);

          router.replace({
            pathname: router.location.pathname,
            query: { ...router.location.query, authMode: 'login' },
          });
        }}
        enrollButtonRef={enrollButtonRef}
        trackingName="logged_out_enroll_button"
        dataE2eName="login-enroll-button"
      />
    );
  } else if (shouldShowGoToCourseButton) {
    return (
      <EnterpriseDisplayEnrollButton
        courseId={pageData?.id}
        // @ts-expect-error DateTime: string
        privateSessionStart={pageData?.fixedSession?.startsAt}
        size={size}
        shouldShowSponsoredByMessage={shouldShowSponsoredByMessage}
        onClick={() => {
          track('click_button', enrollButtonTrackingData);
          onActionSuccess();
        }}
        enrollButtonRef={enrollButtonRef}
        trackingName={ENROLL_BUTTON_TRACKING_ID}
        dataE2eName={`${pageData?.id}~courseHomeLink`}
        customMessage={buttonLabel}
      />
    );
  } else if (showQuickEnrollButton) {
    return (
      <EnterpriseOptimizedEnrollmentButton
        courseId={pageData?.id}
        // @ts-expect-error DateTime: string
        privateSessionStart={pageData?.fixedSession?.startsAt}
        enrollButtonRef={enrollButtonRef}
        onActionSuccess={onActionSuccess}
        shouldShowSponsoredByMessage={shouldShowSponsoredByMessage}
        size={size}
        trackingOnClick={() => {
          track('click_button', enrollButtonTrackingData);
        }}
      />
    );
    // Show multi-program enroll button if user is not member of current program, but is member of other programs in current org and has other options to enroll.
    // We only have this data if user is not a member of the current program.
    // If a learner is domain whitelisted for the program, then this case isn't hit (they go to the default)
  } else if (availableEnrollmentOptions?.programs?.length || availableEnrollmentOptions?.invitedPrograms?.length) {
    return (
      <EnterpriseMultiProgramEnrollButtonNotConfigurable
        courseId={pageData?.id}
        program={program}
        // @ts-expect-error DateTime: string
        privateSessionStart={pageData?.fixedSession?.startsAt}
        enrollButtonRef={enrollButtonRef}
        shouldShowSponsoredByMessage={shouldShowSponsoredByMessage}
        size={size}
        onActionSuccess={onActionSuccess}
        trackingOnClick={() => {
          track('click_button', enrollButtonTrackingData);
        }}
      />
    );
  } else if (!program || !thirdPartyOrganization || !productId || !isMember) {
    return null;
  }

  // This is considered the default/standard enroll button experience, special cases have been handled above.
  return (
    <div css={buttonStyles.buttonWrapper(theme, size)} data-e2e="enroll-button-wrapper">
      <div
        ref={size !== 'small' ? enrollButtonRef : undefined}
        onClick={handleEnrollButtonClick}
        onKeyPress={(e) => a11yKeyPress(e, handleEnrollButtonClick)}
        tabIndex={-1}
        role="button"
        css={shouldShowBiggerCTA ? styles.biggerButton : styles.button}
      >
        <ProgramActionButtonContainer
          programId={program.id}
          onActionSuccess={onActionSuccess}
          thirdPartyOrganizationId={thirdPartyOrganization.id}
          productId={productId}
          productType={ENROLL_PRODUCT_TYPES.COURSE}
          actionType="ENROLL"
          initialBtnLabel={
            // @ts-expect-error DateTime: string
            <StartDateButton courseId={pageData?.id} privateSessionStart={pageData?.fixedSession?.startsAt} />
          }
          shouldResetToDefaultStatus={true}
          apiStatusResetTime={5000}
          deepLinkItemId={deepLinkItemId}
          isProject={courseType !== CourseType.StandardCourse}
          collectionId={router.location?.query?.collectionId}
          btnAttributes={{
            type: 'primary',
            htmlAttributes: {
              'data-e2e': 'EnrollButton',
            },
          }}
          trackingInfo={{
            trackingData: {
              actionType: 'ENROLL',
              courseId: productId,
              ...getClipsTrackingInfo(router.location.query?.fromClip),
            },
            trackingName: ENROLL_BUTTON_TRACKING_ID,
          }}
        />
      </div>
      {shouldShowSponsoredByMessage && (
        <SponsoredByMessage thirdPartyOrgName={thirdPartyOrganization.name} isUpswell={isUpswell} />
      )}
    </div>
  );
};

export default EnterpriseCourseEnrollButtonNotConfigurable;
